import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { PrismComboSelectInput } from '@mylighthouse/prism-ember';
import type { CurrencyConversionExchangeRatesProfile } from 'lumen/models';

type ProfileOption = {
  disabled?: boolean;
  disabledMessage?: string;
  label: string;
  profile: CurrencyConversionExchangeRatesProfile | null;
  value: string;
};

type ProfileGroupOption = {
  groupName: string;
  options: ProfileOption[];
};

interface HotelAndMarketCurrencyProfilePickerSignature {
  Args: {
    isReadOnly?: boolean;
    onProfileChange: (
      profile: CurrencyConversionExchangeRatesProfile | null,
    ) => void;
    profiles: CurrencyConversionExchangeRatesProfile[];
    selectedProfile: CurrencyConversionExchangeRatesProfile | null;
  };
}

export default class HotelAndMarketCurrencyProfilePicker extends Component<HotelAndMarketCurrencyProfilePickerSignature> {
  @service declare currencyConversion: Services['currency-conversion'];
  @service declare intl: Services['intl'];
  @service declare usageAnalytics: Services['usage-analytics'];

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  get options(): ProfileOption[] {
    const profileOptions: ProfileOption[] = [
      {
        label: this.intl.t('currency-conversion.todays-rates'),
        value: 'latest',
        profile: null,
      },
    ];
    const customProfileOptions: ProfileOption[] = this.args.profiles.map(
      (profile) => ({
        label: profile.title,
        value: profile.id!,
        disabled: !profile.isValid,
        disabledMessage:
          profile.hasInvalidRates || profile.hasNoExchangeRates
            ? this.intl.t(
                'currency-conversion.profile-picker.incomplete-profile-message',
              )
            : this.intl.t(
                'currency-conversion.profile-picker.syncing-profile-message',
              ),
        profile,
      }),
    );
    return profileOptions.concat(customProfileOptions);
  }

  @cached
  get profileOptions(): ProfileGroupOption[] {
    return [
      {
        groupName: this.intl.t('currency-conversion.exchange-rates-profiles'),
        options: this.options,
      },
    ];
  }

  get selectedProfileOption(): ProfileOption | undefined {
    if (!this.args.selectedProfile) {
      return this.options.find((option) => option.value === 'latest');
    }

    return this.options.find(
      (option) => option.value === this.args.selectedProfile?.id,
    );
  }

  get trackingViewLevel(): string {
    return this.currencyConversion.isHotelLevel
      ? 'hotel-level'
      : 'organization-level';
  }

  get triggerLabel(): string {
    return this.selectedProfileOption?.label ?? '';
  }

  @action
  handleProfileChange(option: ProfileOption): void {
    this.args.onProfileChange(option.profile);

    this.usageAnalytics.track('Exchange Rate Profile Selected', {
      eventTrigger: 'profile-selected',
      view: this.trackingViewLevel,
    });
  }

  <template>
    <PrismComboSelectInput
      @onChange={{this.handleProfileChange}}
      @options={{this.profileOptions}}
      @unsafe_isClampedToOneLine={{true}}
      @value={{this.selectedProfileOption}}
    />
  </template>
}
